/**
 * Importar cópia antiga da planilha para o histórico (aba Precisão).
 *
 * Por que existe: o ritmo precisa de 26 semanas e a planilha recomeça em
 * janeiro. As cópias que o escritório guardou (e-mail, pendrive) entram como
 * fotos com a data em que foram tiradas; o backend só INSERE términos que
 * ainda não conhece a partir delas (nunca apaga nem altera).
 *
 * A projeção gravada com a cópia é a que a página teria mostrado NAQUELE dia
 * (ritmo com os términos até a data de referência), para a aba Precisão
 * comparar depois o previsto com o que de fato saiu.
 */
import { calcularPainelCapacidade, horizontePadrao, janelaDoRitmo, linhasParaFoto } from "../../utils/capacidade";
import { parsearPlanilhaSemTravar } from "../../utils/parsePlanilhaAsync";
import { listarDiasNaoUteis } from "../../services/calendario";
import { enviarFoto, listarTerminos } from "../../services/planilhaLegado";

export const ORIGEM_IMPORTACAO = "importacao";

function hex(buffer) {
  return [...new Uint8Array(buffer)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * sha256 do arquivo: é a "versão" da cópia (o backend é idempotente por ela,
 * então importar o mesmo arquivo duas vezes não duplica nada).
 */
export async function sha256DoArquivo(arquivo) {
  const subtle = typeof window !== "undefined" ? window.crypto?.subtle : null;
  if (!subtle) {
    throw new Error("Este navegador não consegue calcular a assinatura do arquivo. Use um navegador atualizado.");
  }
  const buffer = await arquivo.arrayBuffer();
  return hex(await subtle.digest("SHA-256", buffer));
}

function mensagemDeLayout(extrato) {
  const faltando = extrato?.faltando || [];
  if (faltando.length) return `Esta cópia não tem as colunas esperadas: falta ${faltando.join(", ")}.`;
  return "Esta cópia não tem o layout esperado da aba GERENCIAL.";
}

/**
 * O calendário que a tela já tem começa em hoje − 420 dias; a janela do ritmo
 * de uma cópia antiga começa 28 semanas antes DELA. Sem ler esse intervalo,
 * os feriados de lá contariam como dia de trabalho e a projeção gravada na
 * foto sairia com dias úteis a mais.
 */
async function lerCalendarioDaData(request, dataReferencia, inicioDaJanela) {
  const resposta = await listarDiasNaoUteis(request, { de: inicioDaJanela, ate: horizontePadrao(dataReferencia) });
  const dias = Array.isArray(resposta?.dias) ? resposta.dias : [];
  const anos = Array.isArray(resposta?.anos_com_registro) ? resposta.anos_com_registro : [];
  return {
    naoUteis: dias.map((dia) => dia.data).filter(Boolean),
    anosComRegistro: anos.map(Number),
  };
}

/**
 * `importarCopiaAntiga({request, arquivo, dataReferencia, naoUteis,
 * anosComRegistro})` → resposta do `POST fotos` (`{id, criada, ...}`).
 * Rejeita com mensagem em português (layout, data, rede).
 */
export async function importarCopiaAntiga({ request, arquivo, dataReferencia, naoUteis, anosComRegistro }) {
  if (!arquivo) throw new Error("Escolha o arquivo da cópia.");
  if (!dataReferencia) throw new Error("Informe a data em que a cópia foi tirada.");

  // A assinatura vem ANTES do parse: o worker recebe o buffer como
  // transferível e ele deixa de existir deste lado.
  const versaoArquivo = await sha256DoArquivo(arquivo);
  const extrato = await parsearPlanilhaSemTravar("capacidade", arquivo);
  if (!extrato?.ok) throw new Error(mensagemDeLayout(extrato));

  const janela = janelaDoRitmo(dataReferencia);
  const [{ terminos }, calendario] = await Promise.all([
    listarTerminos(request, { desde: janela.de }),
    lerCalendarioDaData(request, dataReferencia, janela.de),
  ]);
  const painel = calcularPainelCapacidade({
    extrato,
    terminosBackend: terminos || [],
    fotos: [],
    naoUteis: new Set([...(naoUteis || []), ...calendario.naoUteis]),
    anosComRegistro: new Set([...(anosComRegistro || []), ...calendario.anosComRegistro]),
    hoje: dataReferencia,
  });

  return enviarFoto(request, {
    origem: ORIGEM_IMPORTACAO,
    versao_arquivo: versaoArquivo,
    versao_leitor: extrato.versaoLeitor,
    data_referencia: dataReferencia,
    nome_arquivo: arquivo.name || "",
    linhas: linhasParaFoto(extrato.linhas),
    projecao: painel.projecaoFoto || null,
  });
}
